import { Link, useParams } from "react-router-dom";
import { blacklistEntries } from "@/mocks/blacklist";
import type { RiskEntry } from "@/types/content";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import RiskBadge from "@/components/feature/RiskBadge";
import RiskRecordCard from "@/components/feature/RiskRecordCard";

const SITE = "https://son4min.cn";

const LEVEL_THEME: Record<string, { band: string; iconBg: string }> = {
  high: { band: "border-accent-200 bg-accent-50", iconBg: "bg-accent-500" },
  mid: { band: "border-primary-200 bg-primary-50", iconBg: "bg-primary-500" },
  watch: { band: "border-secondary-200 bg-secondary-50", iconBg: "bg-secondary-500" },
};

const SECTIONS = [
  { id: "summary", label: "风险摘要" },
  { id: "signals", label: "关键风险点" },
  { id: "timeline", label: "风险时间线" },
  { id: "complaints", label: "投诉分布" },
  { id: "spot", label: "识别特征" },
  { id: "advice", label: "维权建议" },
];

export default function BlacklistDetail() {
  const { id } = useParams();
  const entry = (blacklistEntries as RiskEntry[]).find((item) => item.id === id);

  if (!entry) {
    return (
      <>
        <PageMeta
          title="记录不存在或已下架 - 避雷哨"
          description="你访问的风险记录不存在或已下架，可返回避雷名单继续查询其他平台。"
          path="/blacklist"
        />
        <section className="mx-auto flex max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">这条风险记录不存在或已下架</h1>
          <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-foreground-600">
            记录可能已被更正或合并。你可以返回名单页继续查询其他平台，也可以通过联系页面向我们反馈。
          </p>
          <Link
            to="/blacklist"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回避雷名单
          </Link>
        </section>
      </>
    );
  }

  const sameCategory = (blacklistEntries as RiskEntry[]).filter(
    (item) => item.categoryKey === entry.categoryKey && item.id !== entry.id,
  );
  const others = (blacklistEntries as RiskEntry[]).filter(
    (item) => item.categoryKey !== entry.categoryKey && item.id !== entry.id,
  );
  const related = [...sameCategory, ...others].slice(0, 3);
  const theme = LEVEL_THEME[entry.riskLevel] || LEVEL_THEME.watch;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `${entry.name} 风险记录：${entry.riskText}`,
      description: entry.summary,
      inLanguage: "zh-CN",
      datePublished: entry.reportedAt,
      dateModified: entry.updatedAt,
      articleSection: entry.category,
      keywords: entry.tags.join(","),
      author: { "@type": "Organization", name: "避雷哨" },
      publisher: { "@type": "Organization", name: "避雷哨", url: `${SITE}/` },
      mainEntityOfPage: `${SITE}/blacklist/${entry.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "避雷名单", item: `${SITE}/blacklist` },
        { "@type": "ListItem", position: 3, name: entry.name, item: `${SITE}/blacklist/${entry.id}` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title={`${entry.name} 风险记录_${entry.riskText}_避雷提醒 - 避雷哨`}
        description={`${entry.name}（${entry.domain}）风险记录：${entry.summary}`.slice(0, 150)}
        keywords={`${entry.name},${entry.category},${entry.tags.join(",")},避雷名单,网站黑料`}
        path={`/blacklist/${entry.id}`}
        jsonLd={jsonLd}
      />

      <section className={`border-b ${theme.band}`}>
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "避雷名单", to: "/blacklist" }, { name: entry.name }]} />
          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg text-background-50 ${theme.iconBg}`}
              >
                <i className="ri-alert-line text-2xl" />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{entry.name}</h1>
                  <RiskBadge level={entry.riskLevel} text={entry.riskText} size="md" />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-label text-sm text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-global-line" />
                    {entry.domain}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-price-tag-3-line" />
                    {entry.category}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" />
                    {entry.views.toLocaleString()} 次查看
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:shrink-0">
              {[
                { label: "收录时间", value: entry.reportedAt },
                { label: "最近更新", value: entry.updatedAt },
                { label: "投诉数量", value: `${entry.complaintCount} 条` },
                { label: "涉及金额", value: entry.amountLevel },
              ].map((item) => (
                <div key={item.label} className="rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5">
                  <p className="font-label text-xs text-foreground-500">{item.label}</p>
                  <p className="mt-1 font-label text-xs font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.7fr_1fr] lg:gap-12">
          <article className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src={entry.cover}
                alt={`${entry.name} 风险记录示意图`}
                title={`${entry.name} 风险记录 - 网站黑料避雷`}
                className="h-56 w-full object-cover md:h-72"
              />
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {entry.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 font-label text-xs text-foreground-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <section id="summary" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">风险摘要</h2>
              <p className="mt-3 font-body text-base leading-relaxed text-foreground-700">{entry.summary}</p>
            </section>

            <section id="signals" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">关键风险点</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {entry.signals.map((signal) => (
                  <div key={signal.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                        <i className="ri-error-warning-line" />
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{signal.title}</h3>
                    </div>
                    <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">{signal.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="timeline" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">风险时间线</h2>
              <ol className="mt-4 space-y-0">
                {entry.timeline.map((item, index) => (
                  <li key={`${item.date}-${index}`} className="relative flex gap-4 pb-6 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <i className="ri-record-circle-line text-sm" />
                      </span>
                      {index < entry.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300" />}
                    </div>
                    <div className="pb-1">
                      <p className="font-label text-xs font-medium text-primary-700">{item.date}</p>
                      <h3 className="mt-1 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="complaints" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">投诉类型分布</h2>
              <div className="mt-4 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                {entry.complaintStats.map((stat) => (
                  <div key={stat.label}>
                    <div className="flex items-center justify-between font-label text-sm">
                      <span className="text-foreground-800">{stat.label}</span>
                      <span className="font-medium text-foreground-600">{stat.value}%</span>
                    </div>
                    <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${stat.value}%` }} />
                    </div>
                  </div>
                ))}
                <p className="pt-1 font-label text-xs text-foreground-500">
                  数据来源：用户提交反馈的类型占比，按最近一次更新统计。
                </p>
              </div>
            </section>

            <section id="spot" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">如何识别同类平台</h2>
              <ul className="mt-4 space-y-3">
                {entry.howToSpot.map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-lg bg-background-100 px-5 py-4">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                      <i className="ri-check-line text-sm" />
                    </span>
                    <span className="font-body text-sm leading-relaxed text-foreground-700">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section id="advice" className="mt-10 scroll-mt-28">
              <h2 className="font-heading text-xl font-bold text-foreground-950">如果已经发生交易</h2>
              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <ol className="space-y-3">
                  {entry.advice.map((item, index) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 font-label text-xs font-bold text-background-50">
                        {index + 1}
                      </span>
                      <span className="font-body text-sm leading-relaxed text-foreground-800">{item}</span>
                    </li>
                  ))}
                </ol>
                <Link
                  to="/guides/report-channels-list"
                  className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                >
                  <i className="ri-share-forward-line" />
                  查看举报渠道清单
                </Link>
              </div>
            </section>

            <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-information-line text-primary-600" />
                内容说明与免责声明
              </h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                本页内容基于用户提交反馈与公开信息整理，描述的是被反馈的现象与时间线，不构成对相关主体的定性结论。平台经营情况可能发生变化，请结合最新实际情况判断。如你认为信息有误，可通过联系页面提交更正申请，我们会在核实后及时更新。
              </p>
            </section>
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">本页目录</h2>
              <nav aria-label="本页目录" className="mt-3 flex flex-col">
                {SECTIONS.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 border-l-2 border-background-200 py-2 pl-3 font-label text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                    {section.label}
                  </a>
                ))}
              </nav>
            </div>

            <div className={`rounded-lg border p-5 ${theme.band}`}>
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-shield-star-line text-primary-700" />
                查询结论速览
              </h2>
              <dl className="mt-4 space-y-3 font-label text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-600">风险等级</dt>
                  <dd>
                    <RiskBadge level={entry.riskLevel} text={entry.riskText} />
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-600">反馈数量</dt>
                  <dd className="font-medium text-foreground-900">{entry.complaintCount} 条</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-600">最近更新</dt>
                  <dd className="font-medium text-foreground-900">{entry.updatedAt}</dd>
                </div>
              </dl>
              <Link
                to="/contact"
                className="mt-5 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-circle-line" />
                补充线索或申请更正
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关风险记录</h2>
              <div className="mt-3 flex flex-col gap-2">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/blacklist/${item.id}`}
                    className="group flex items-center justify-between gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
                  >
                    <span className="min-w-0">
                      <span className="block font-label text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                        {item.name}
                      </span>
                      <span className="mt-0.5 block font-label text-xs text-foreground-500">{item.category}</span>
                    </span>
                    <i className="ri-arrow-right-s-line text-lg text-foreground-400 group-hover:text-primary-600" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">更多同类记录</h2>
              <p className="mt-2 font-body text-sm text-foreground-600">
                同类型的风险表现往往相似，对照着看更容易发现共同点。
              </p>
            </div>
            <Link
              to={`/blacklist?category=${entry.categoryKey}`}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-label text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
            >
              查看{entry.category}全部记录
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <RiskRecordCard key={item.id} entry={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}